import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowRight,
  FaCircleCheck,
  FaBolt,
  FaClock,
} from "react-icons/fa6";
import { GoInfo } from "react-icons/go";
import {
  getUscisFeeQuestionnaire,
  submitUscisFeeQuestionnaire,
  setUscisFeeDecision,
  getApplication,
} from "../../API/applicationAPI";
import type {
  UscisFeeQuestionnaire as UscisFeeQuestionnaireType,
  UscisFeeQuestion,
  UscisFeeAnswer,
} from "../../types/application";
import { createCheckoutSession } from "../../API/paymentAPI";
import {
  buttonStyleSm2,
  buttonStyleSecondarySm22,
  formatCurrency,
} from "../../help/Utils";
import PovertyGuidelineHelp from "./PovertyGuidelineHelp";
import { getPackageById, Package as SelectedPackage } from "../../API/packageAPI";
import ButtonLoadingLabel from "../../components/ButtonLoadingLabel";

const UscisFeeQuestionnaire: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questionnaire, setQuestionnaire] =
    useState<UscisFeeQuestionnaireType | null>(null);
  const [answers, setAnswers] = useState<
    Record<string, boolean | string | number>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState(false);
  const [feeComputation, setFeeComputation] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] =
    useState<SelectedPackage | null>(null);
  const [packageLoading, setPackageLoading] = useState(false);

  const packageFee =
    !packageLoading && selectedPackage?.price != null
      ? Number(selectedPackage.price)
      : null;

  const computedFee =
    feeComputation?.breakdown?.total ?? feeComputation?.amount ?? null;

  const renderAmount = (amount: number | null, widthClass = "w-24") => {
    if (amount == null) {
      return (
        <span className="inline-block relative overflow-hidden rounded">
          <span className={`inline-block h-4 ${widthClass} bg-gray-200`} />
          <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </span>
      );
    }
    return formatCurrency(amount);
  };

  const priceMarker = (
    icon: React.ReactNode,
    label: string,
    colorClass: string
  ) => (
    <span className={`inline-flex items-center gap-1.5 ${colorClass}`}>
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-current/10">
        {icon}
      </span>
      <span>{label}</span>
    </span>
  );

  const FeeBreakdown = ({ includeUscis }: { includeUscis: boolean }) => {
    const total =
      packageFee == null || (includeUscis && computedFee == null)
        ? null
        : packageFee + (includeUscis ? computedFee ?? 0 : 0);

    return (
      <div className="mb-5 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4 text-[#1B365D]">
        {selectedPackage?.name ? (
          <p className="mb-2 text-[12px] font-semibold text-[#64748B]">
            {selectedPackage.name}
          </p>
        ) : null}
        {includeUscis ? (
          <>
            <p className="text-[18px] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
              {priceMarker(
                <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
                `${renderAmount(total, "w-28")} Now`,
                "text-[#1B365D]"
              )}
            </p>
            <p className="mt-1 text-[13px] text-[#64748B]">
              {priceMarker(
                <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
                `${renderAmount(packageFee, "w-20")}`,
                "text-[#1B365D]"
              )}{" "}
              +{" "}
              {priceMarker(
                <FaClock className="h-3 w-3 text-amber-500" aria-hidden />,
                `${renderAmount(computedFee, "w-20")}`,
                "text-[#1B365D]"
              )}
            </p>
          </>
        ) : (
          <p className="text-[18px] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
            {priceMarker(
              <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
              `${renderAmount(packageFee, "w-24")} Now`,
              "text-[#1B365D]"
            )}
            <span>,</span>
            {priceMarker(
              <FaClock className="h-3 w-3 text-amber-500" aria-hidden />,
              `${renderAmount(computedFee, "w-24")} Later`,
              "text-[#1B365D]"
            )}
          </p>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!id) {
        setError("Application ID is required");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getUscisFeeQuestionnaire(parseInt(id));
        setQuestionnaire(data);

        try {
          const application = await getApplication(parseInt(id));
          if (application.packageId) {
            setPackageLoading(true);
            const pkg = await getPackageById(application.packageId);
            setSelectedPackage(pkg);
          }
        } catch (packageErr) {
          console.warn("Could not load selected package:", packageErr);
          setSelectedPackage(null);
        } finally {
          setPackageLoading(false);
        }
      } catch (err: any) {
        console.error("Error fetching questionnaire:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load questionnaire. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [id]);

  const handleAnswerChange = (
    key: string,
    value: boolean | string | number
  ) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    // Clear error for this field when user starts typing
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!questionnaire) return false;

    const newErrors: Record<string, string> = {};

    questionnaire.questions.forEach((question) => {
      if (question.required) {
        const value = answers[question.key];
        if (value === undefined || value === null || value === "") {
          newErrors[question.key] = "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!questionnaire || !id) return;

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Convert answers to answer array format
      const answerArray: UscisFeeAnswer[] = questionnaire.questions.map(
        (question) => ({
          key: question.key,
          value:
            answers[question.key] ?? (question.type === "boolean" ? false : ""),
        })
      );

      // Submit questionnaire first to compute the fee
      const result = await submitUscisFeeQuestionnaire(
        parseInt(id),
        answerArray
      );

      // Store the computed fee to display in decision options
      if (result.feeComputation) {
        setFeeComputation(result.feeComputation);
      }

      // Show decision options with computed fee
      setShowDecision(true);
      setSubmitting(false);
    } catch (err: any) {
      console.error("Error submitting questionnaire:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to submit questionnaire. Please try again."
      );
      setSubmitting(false);
    }
  };

  const handleDecision = async (optIn: boolean) => {
    if (!questionnaire || !id) return;

    try {
      setSubmitting(true);
      setError(null);

      // Convert answers to answer array format (answers already submitted, but we pass them again for setUscisFeeDecision)
      const answerArray: UscisFeeAnswer[] = questionnaire.questions.map(
        (question) => ({
          key: question.key,
          value:
            answers[question.key] ?? (question.type === "boolean" ? false : ""),
        })
      );

      // Construct success and cancel URLs for Stripe checkout session (only if opting in)
      const successUrl = optIn
        ? `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`
        : undefined;
      const cancelUrl = optIn
        ? `${window.location.origin}/dashboard?payment_status=cancel`
        : undefined;

      // Set the decision (questionnaire already submitted, so this just sets the optIn decision)
      const result = await setUscisFeeDecision(
        parseInt(id),
        optIn,
        answerArray,
        successUrl,
        cancelUrl
      );

      console.log("Decision result:", result);

      if (optIn) {
        // If opting in, backend creates checkout session with USCIS fee + package
        // Redirect to Stripe checkout
        if (result.checkoutUrl) {
          console.log("Redirecting to checkout URL:", result.checkoutUrl);
          window.location.href = result.checkoutUrl;
        } else {
          // If no checkout URL returned, log and show error
          console.error("No checkout URL returned from backend:", result);
          setError("Failed to create checkout session. Please try again.");
          setSubmitting(false);
        }
      } else {
        // If opting out (Pay Later), create checkout session for package only
        // First, get the application to get packageId
        const application = await getApplication(parseInt(id));

        if (!application.packageId) {
          setError("Package not found. Cannot proceed to checkout.");
          setSubmitting(false);
          return;
        }

        // Create checkout session for package only (without USCIS fees)
        const packageSuccessUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
        const packageCancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

        console.log("Creating checkout session for package only:", {
          applicationId: parseInt(id),
          packageId: application.packageId,
        });

        const session = await createCheckoutSession(
          parseInt(id),
          application.packageId,
          packageSuccessUrl,
          packageCancelUrl
        );

        console.log("Checkout session created:", session);

        // Redirect to Stripe checkout
        if (session.sessionUrl) {
          window.location.href = session.sessionUrl;
        } else {
          console.error("No session URL returned:", session);
          setError("Failed to create checkout session. Please try again.");
          setSubmitting(false);
        }
      }
    } catch (err: any) {
      console.error("Error setting USCIS fee decision:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to process your decision. Please try again."
      );
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: UscisFeeQuestion) => {
    const value = answers[question.key];
    const hasError = !!errors[question.key];

    switch (question.type) {
      case "boolean":
        return (
          <div className="w-full">
            <h2 className="text-[20px] md:text-[24px] text-[#1e2b5e] font-semibold text-left mb-4">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>
            <div className="space-y-3">
              {[true, false].map((option) => {
                const isSelected = value === option;
                return (
                  <label
                    key={option ? "yes" : "no"}
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                      isSelected
                        ? "border-[#1e2b5e] bg-[#f1f2f6]"
                        : "border-gray-200 hover:border-[#1e2b5e]"
                    }`}
                  >
                    <input
                      type="radio"
                      name={question.key}
                      checked={isSelected}
                      onChange={() => handleAnswerChange(question.key, option)}
                      className="mr-4 h-5 w-5"
                      style={{ accentColor: "#1e2b5e" }}
                    />
                    <span className="text-[#1B365D] text-base font-medium">
                      {option ? "Yes" : "No"}
                    </span>
                  </label>
                );
              })}
            </div>
            {question.helpText && (
              <div className="mt-4 bg-[#f1f2f6] border-l-4 border-[#1e2b5e] rounded-xl p-4 flex items-start">
                <GoInfo size={18} className="text-[#1e2b5e] mr-3 mt-1" />
                <p className="text-sm text-[#1B365D]">{question.helpText}</p>
              </div>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-2">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "select":
        return (
          <div className="w-full">
            <h2 className="text-[20px] md:text-[24px] text-[#1e2b5e] font-semibold text-left mb-4">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>
            <select
              value={(value as string) || ""}
              onChange={(e) => handleAnswerChange(question.key, e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1e2b5e] bg-white ${
                hasError ? "border-red-500" : "border-gray-200"
              }`}
            >
              <option value="">Select an option</option>
              {question.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {question.helpText && (
              <div className="mt-4 bg-[#f1f2f6] border-l-4 border-[#1e2b5e] rounded-xl p-4 flex items-start">
                <GoInfo size={18} className="text-[#1e2b5e] mr-3 mt-1" />
                <p className="text-sm text-[#1B365D]">{question.helpText}</p>
              </div>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-2">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div className="w-full">
            <h2 className="text-[20px] md:text-[24px] text-[#1e2b5e] font-semibold text-left mb-4">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>
            <input
              type="number"
              value={(value as number) || ""}
              onChange={(e) =>
                handleAnswerChange(
                  question.key,
                  e.target.value ? parseFloat(e.target.value) : ""
                )
              }
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1e2b5e] bg-white ${
                hasError ? "border-red-500" : "border-gray-200"
              }`}
            />
            {question.helpText && (
              <div className="mt-4 bg-[#f1f2f6] border-l-4 border-[#1e2b5e] rounded-xl p-4 flex items-start">
                <GoInfo size={18} className="text-[#1e2b5e] mr-3 mt-1" />
                <p className="text-sm text-[#1B365D]">{question.helpText}</p>
              </div>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-2">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "text":
      default:
        return (
          <div className="w-full">
            <h2 className="text-[20px] md:text-[24px] text-[#1e2b5e] font-semibold text-left mb-4">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>
            <input
              type="text"
              value={(value as string) || ""}
              onChange={(e) => handleAnswerChange(question.key, e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1e2b5e] bg-white ${
                hasError ? "border-red-500" : "border-gray-200"
              }`}
            />
            {question.helpText && (
              <div className="mt-4 bg-[#f1f2f6] border-l-4 border-[#1e2b5e] rounded-xl p-4 flex items-start">
                <GoInfo size={18} className="text-[#1e2b5e] mr-3 mt-1" />
                <p className="text-sm text-[#1B365D]">{question.helpText}</p>
              </div>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-2">
                {errors[question.key]}
              </p>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-4">
        <div className="w-full max-w-2xl bg-white shadow-md rounded-2xl p-[40px] text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#1B365D] mx-auto mb-6"></div>
          <p className="text-[#1B365D] text-lg font-semibold">
            Loading questionnaire...
          </p>
        </div>
      </div>
    );
  }

  if (error && !questionnaire) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-4">
        <div className="w-full max-w-2xl bg-white shadow-md rounded-2xl p-[40px] text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-[#1B365D] mb-3">
            Error Loading Questionnaire
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#F4A261] text-[#1B365D] font-semibold px-8 py-3 rounded-full hover:bg-[#F29449] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return null;
  }

  const canSubmit =
    !!questionnaire &&
    questionnaire.questions.every((q) => {
      if (!q.required) return true;
      const v = answers[q.key];
      if (q.type === "boolean") return typeof v === "boolean";
      return v !== undefined && v !== null && v !== "";
    });

  // Show decision options if questionnaire is validated
  if (showDecision) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-start py-10 px-4 pt-[200px]">
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="font-bold text-center mb-4 lg:text-[40px] md:text-[30px] text-[26px]">
            Apply with confidence: guaranteed approval or your money back
          </h1>
          <p className="text-center mb-8 text-[#1B365DCC]">
            Reduce your risk of application rejection or denial by 25% with
            step-by-step guidance, live support, and quality reviews by an
            experienced team.
          </p>
          <p className="flex items-center mb-10 justify-center">
            Select USCIS fee option{" "}
            <FaCircleCheck className="ml-3 text-[#00B894]" />
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pay Later Option */}
            <div className="border border-[#D1D5DB] rounded-2xl p-[25px]">
              <p className="flex items-center mb-4">
                <FaClock className="text-[28px] me-3 text-amber-500" /> Pay USCIS fees later
              </p>
              <p className="text-[12px] text-[#6B7280] mb-4">HOW IT WORKS</p>
              <p className="text-[14px] text-[#1B365DCC] mb-4">
                Pay by credit card, or mail us a check to send with your
                application. You'll need to pay USCIS when we ship your
                application.
              </p>
              <FeeBreakdown includeUscis={false} />
              <button
                className="w-full flex justify-center items-center"
                style={buttonStyleSm2}
                onClick={() => handleDecision(false)}
                disabled={submitting}
              >
                {submitting ? <ButtonLoadingLabel label="Processing" /> : "Select"}{" "}
                {!submitting && <FaArrowRight className="ms-4" />}
              </button>
            </div>

            {/* Pay Now Option (Recommended) */}
            <div className="border-2 border-[#00B894] rounded-2xl p-[25px] relative">
              <label className="text-[12px] bg-[#00B894] text-white py-1 px-3 rounded-[20px] absolute top-[-15px] left-1/2 transform -translate-x-1/2">
                RECOMMENDED
              </label>
              <p className="flex items-center mb-4">
                <FaBolt className="text-[28px] me-3 text-emerald-600" /> Pay USCIS fees now
              </p>
              <p className="text-[12px] text-[#6B7280] mb-4">&nbsp;</p>
              <p className="text-[14px] text-[#1B365DCC] mb-4">
                Pay via credit or debit, with an option to pay over 12 months.
                We'll write the check to USCIS for you.
              </p>
              <FeeBreakdown includeUscis />
              <button
                className="w-full flex justify-center items-center"
                style={buttonStyleSecondarySm22}
                onClick={() => handleDecision(true)}
                disabled={submitting}
              >
                {submitting ? <ButtonLoadingLabel label="Loading" /> : "Select"}{" "}
                {!submitting && <FaArrowRight className="ms-4" />}
              </button>
              <p className="text-[12px] text-[#6B7280] text-center mt-4">
                Includes your package fee and USCIS fee in one checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-start py-10 px-4 pt-[200px]">
      <div className="w-full max-w-2xl mx-auto">
        {questionnaire.description && (
          <p className="text-center text-[#1B365D] mb-6">
            {questionnaire.description}
          </p>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <PovertyGuidelineHelp />

          {questionnaire.questions.map((question) => (
            <div
              key={question.key}
              className="border border-gray-200 rounded-2xl p-6 md:p-8 bg-white shadow-sm"
            >
              {renderQuestion(question)}
            </div>
          ))}

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`flex items-center justify-center px-6 md:px-10 py-3 md:py-4 rounded-full text-lg font-semibold border-2 transition-colors ${
                submitting || !canSubmit
                  ? "bg-gray-300 border-gray-300 text-white cursor-not-allowed"
                  : "bg-[#F4A261] border-[#F4A261] text-[#1B365D] hover:bg-[#F29449]"
              }`}
              disabled={submitting || !canSubmit}
            >
              {submitting ? <ButtonLoadingLabel label="Submitting" /> : "Continue"}
              {!submitting && <FaArrowRight className="ml-3" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UscisFeeQuestionnaire;
